// Packages and components
import React, { useState } from 'react';
import { Box } from 'grommet';
import SHDButton from '@ui/Button/SHDButton';
import SHDHeading from '@ui/Heading/SHDHeading';
import SHDInputPhoneNumber from '@ui/InputPhoneNumber/SHDInputPhoneNumber';
import SHDText from '@ui/Text/SHDText';

// Helpers
import { isValidPhoneNumber } from 'react-phone-number-input';
import { useAdminUpdatePhoneNumber } from '@hooks/useAdmin';

// Types and interfaces
import type { AcceptTosProps } from '../../admin.types';
import type { ErrorMessage } from '@theme/theme.d';

const UpdatePhoneNumber: React.FC<AcceptTosProps> = ({ userId }) => {
  // States
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [isGoodPhoneNumber, setIsGoodPhoneNumber] = useState<boolean>(false);
  const [error, setError] = useState<ErrorMessage | null>(null);

  // Hooks
  const {
    updatePhoneNumber,
    error: updatePhoneNumberError,
    success: updatePhoneNumberSuccess,
  } = useAdminUpdatePhoneNumber();

  // Handlers
  const handleValidatePhoneNumber = () => {
    if (phoneNumber === '') {
      setError(null);
      setIsGoodPhoneNumber(false);
      return;
    }

    if (!isValidPhoneNumber(phoneNumber)) {
      setIsGoodPhoneNumber(false);
      setError({ message: 'Phone number region is unsupported.' });
      return;
    }
    setError(null);
    setIsGoodPhoneNumber(true);
  };

  const handleUpdatePhoneNumber = async () => {
    await updatePhoneNumber(userId, phoneNumber);
  };

  return (
    <Box gap="medium" direction="column" className="update-phone-number">
      <Box direction="column" gap="xxsmall">
        <SHDHeading level={4}>Update phone number</SHDHeading>
      </Box>

      {updatePhoneNumberError && (
        <Box
          background="dk-red-alpha-10"
          border={{ side: 'all', color: 'dk-red-alpha-20' }}
          pad="medium"
          round="xsmall"
        >
          <SHDText color="dk-red-800">{updatePhoneNumberError.message}</SHDText>
        </Box>
      )}

      {updatePhoneNumberSuccess && (
        <Box
          background="dk-green-alpha-10"
          border={{ side: 'all', color: 'shd-green-dark-alpha-30' }}
          pad="medium"
          round="xsmall"
        >
          <SHDText color="dk-green-800">
            {updatePhoneNumberSuccess.message}
          </SHDText>
        </Box>
      )}

      <SHDInputPhoneNumber
        label="Phone number"
        phoneNumber={phoneNumber}
        setPhoneNumber={setPhoneNumber}
        error={error}
        onFocusOut={handleValidatePhoneNumber}
        onChange={(value: string) => setPhoneNumber(value)}
      />
      <SHDButton
        primary
        label="Update"
        disabled={!isGoodPhoneNumber}
        onClick={handleUpdatePhoneNumber}
      />
    </Box>
  );
};

export default UpdatePhoneNumber;
